/**
 * Care Concierge invitation modal (Feature 106).
 *
 * The advisor picks who to invite and confirms. For a sponsored consultation the session is spent
 * the moment this is confirmed, so the copy says so plainly rather than after the fact.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { useClients } from '@/features/client-relationships';
import type {
  AdvisorCreditSummary,
  CreateCasePayload,
  CreditPoolSource,
  CasePayer,
  SchedulingMode,
} from '../../api/careConciergeApi';

const OTHER = '__other';
/** No client chosen yet. Not a valid submission — see handleConfirm. */
const UNCHOSEN = '';

type InviteToConsultationModalProps = {
  payer: CasePayer;
  credit: AdvisorCreditSummary;
  isSending: boolean;
  onConfirm: (payload: CreateCasePayload) => void;
  onClose: () => void;
};

export const InviteToConsultationModal = ({
  payer,
  credit,
  isSending,
  onConfirm,
  onClose,
}: InviteToConsultationModalProps) => {
  const { data: clientsResponse } = useClients();
  const clients = clientsResponse?.data ?? [];

  /**
   * Starts unchosen, and nothing is pre-selected for the advisor.
   *
   * Defaulting to the first client would save a click and cost the occasional invitation sent to
   * the wrong person: confirming here spends a session and emails a real client, so the recipient
   * should be something the advisor picked rather than something they failed to notice.
   *
   * It also sidesteps what this used to do — seed from clients[0] in a useState initializer, which
   * runs before useClients resolves, capturing an empty list and settling on the manual-email path
   * for good.
   */
  const [selected, setSelected] = useState<string>(UNCHOSEN);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [pool, setPool] = useState<CreditPoolSource>(
    credit.firm.available ? 'FIRM' : 'USER',
  );
  const [schedulingMode, setSchedulingMode] = useState<SchedulingMode>('SEND_INVITATION');
  const [error, setError] = useState<string | null>(null);

  const isSponsored = payer === 'SPONSOR';
  const advisorBooks = schedulingMode === 'ADVISOR_BOOKS';
  const invitingSomeoneElse = selected === OTHER;
  const client = clients.find((candidate) => candidate.id === selected);

  const handleConfirm = () => {
    if (selected === UNCHOSEN) {
      setError('Choose who this invitation is for.');
      return;
    }

    const inviteeEmail = invitingSomeoneElse ? email.trim() : (client?.email ?? '');

    // Required on both paths: it is how the case identifies the client, and how a later resend
    // reaches them if the advisor changes their mind about booking.
    if (!inviteeEmail) {
      setError(
        advisorBooks
          ? "Enter your client's email address so the case knows who it is about."
          : 'Enter the email address the invitation should go to.',
      );
      return;
    }

    const inviteeName = invitingSomeoneElse
      ? name.trim()
      : [client?.firstName, client?.lastName].filter(Boolean).join(' ');

    onConfirm({
      payer,
      creditSource: isSponsored ? pool : undefined,
      clientUserId: invitingSomeoneElse ? undefined : client?.id,
      inviteeEmail,
      inviteeName: inviteeName || undefined,
      schedulingMode,
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isSponsored ? 'Sponsor a consultation' : 'Recommend a consultation'}
          </DialogTitle>
          <DialogDescription>
            {isSponsored
              ? 'Uses one of your sponsored sessions as soon as you confirm, whichever way it is scheduled. Your client pays nothing.'
              : 'Your client arranges payment with bQuest directly. No session is used.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="cc-invite-client">Client</Label>
            <select
              id="cc-invite-client"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={selected}
              onChange={(event) => {
                setSelected(event.target.value);
                setError(null);
              }}
              disabled={isSending}
            >
              <option value={UNCHOSEN}>Select a client…</option>
              {clients.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {[candidate.firstName, candidate.lastName].filter(Boolean).join(' ')}
                </option>
              ))}
              <option value={OTHER}>Someone else (enter email)</option>
            </select>
          </div>

          {invitingSomeoneElse && (
            <>
              <div>
                <Label htmlFor="cc-invite-email">Email</Label>
                <Input
                  id="cc-invite-email"
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setError(null);
                  }}
                  disabled={isSending}
                />
              </div>
              <div>
                <Label htmlFor="cc-invite-name">Name (optional)</Label>
                <Input
                  id="cc-invite-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={isSending}
                />
              </div>
            </>
          )}

          {/* Only asked when both pools are funded. With one funded there is a single correct
              answer, and a dialog with one answer is a step, not a choice. */}
          {isSponsored && credit.mustChoosePool && (
            <fieldset>
              <legend className="text-sm font-medium">Use a session from</legend>
              <div className="mt-2 space-y-2">
                {(
                  [
                    {
                      value: 'FIRM' as const,
                      label: credit.firm.firmName ?? 'My firm',
                      remaining: credit.firm.remaining,
                    },
                    {
                      value: 'USER' as const,
                      label: 'My own sessions',
                      remaining: credit.advisor.remaining,
                    },
                  ] satisfies Array<{ value: CreditPoolSource; label: string; remaining: number }>
                ).map((option) => (
                  <label key={option.value} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="cc-invite-pool"
                      value={option.value}
                      checked={pool === option.value}
                      onChange={() => setPool(option.value)}
                      disabled={isSending}
                    />
                    <span>
                      {option.label}{' '}
                      <span className="text-muted-foreground">
                        ({option.remaining} left)
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {/* Requirement v4 item 1. Both paths commit the session now; what differs is who does
              the booking. Said plainly, because an advisor who thinks booking for the client is
              the "free" option would be wrong. */}
          <fieldset>
            <legend className="text-sm font-medium">Scheduling</legend>
            <div className="mt-2 space-y-2">
              {(
                [
                  {
                    value: 'SEND_INVITATION' as const,
                    label: 'Send the client the booking link',
                    hint: 'They receive an email and pick their own time.',
                  },
                  {
                    value: 'ADVISOR_BOOKS' as const,
                    label: 'Schedule on their behalf',
                    hint: 'Opens Calendly here. Your client is not emailed.',
                  },
                ] satisfies Array<{ value: SchedulingMode; label: string; hint: string }>
              ).map((option) => (
                <label key={option.value} className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="cc-invite-scheduling"
                    className="mt-1"
                    value={option.value}
                    checked={schedulingMode === option.value}
                    onChange={() => setSchedulingMode(option.value)}
                    disabled={isSending}
                  />
                  <span>
                    {option.label}
                    <span className="block text-xs text-muted-foreground">{option.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isSending}>
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={isSending}>
            {isSending ? 'Working…' : advisorBooks ? 'Open Calendly' : 'Send invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
